import React from 'react';
import core, { View } from '@gin-betting/core';

import { useMemoSelector } from 'hooks';

import './promoMatchOdds.scss';

const {
    utils: { getHomeAwayOdds },
    entities: {
        selectors: { selectOddsByOddIds },
    },
} = core;

interface Props {
    oddIds?: string[];
    competitors: View.CompetitorsValid;
    OddButton: React.ComponentType<{
        oddId: string | undefined;
        withTitle?: boolean;
    }>;
    showVsBlock?: boolean;
}

const stub: Pick<View.Odd, 'id' | 'name'> = {
    id: '',
    name: '',
} as const;

const minNumberOfOdds = 2 as const;

const PromoMatchOdds: React.FC<Props> = ({
    competitors,
    OddButton,
    oddIds = [],
    showVsBlock = true,
}) => {
    const selectedOdds = useMemoSelector(selectOddsByOddIds, {
        oddIds,
    });

    const odds = selectedOdds.map(i => i ?? stub);

    const numberOfOddStubs = minNumberOfOdds - odds.length;

    if (numberOfOddStubs > 0) {
        odds.push(...Array(numberOfOddStubs).fill(stub));
    }

    const showNames = odds[0]?.name !== competitors.homeName;

    return (
        <div className="__app-PromoMatchOdds-container" styleName="container">
            {odds.map(({ id }, idx) => (
                <div
                    styleName="odd"
                    className="__app-PromoMatchOdds-odd"
                    key={id || idx}
                    style={{ order: 2 * (idx + 1) - 1 }}
                >
                    <OddButton oddId={id} withTitle={false} />
                    {showNames && (
                        <OddName
                            oddId={id}
                            odds={odds}
                            competitors={competitors}
                        />
                    )}
                </div>
            ))}

            {odds.length === 2 && showVsBlock && (
                <div styleName="vs" className="__app-PromoMatchOdds-vs">
                    VS
                </div>
            )}
        </div>
    );
};

interface OddNameProps {
    oddId: string;
    odds: Array<Pick<View.Odd, 'id' | 'name'>>;
    competitors: View.CompetitorsValid;
}

const OddName: React.FC<OddNameProps> = ({ oddId, odds, competitors }) => {
    const { homeOdd, awayOdd } = getHomeAwayOdds({ odds });

    let name = '';

    if (homeOdd?.id === oddId) {
        name = homeOdd.name.replace(competitors.homeName, '');
    }

    if (awayOdd?.id === oddId) {
        name = awayOdd.name.replace(competitors.awayName, '');
    }

    if (!name) return null;

    return (
        <div styleName="odd-name" title={name}>
            {name}
        </div>
    );
};

export default React.memo(PromoMatchOdds);
